import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, Wand2 } from "lucide-react";
import api from "@/lib/api";

const EXAMPLES = [
  "My kitchen sink is leaking under the cabinet",
  "Need a deep clean of a 3 bedroom house before move-in",
  "Logo and brand colors for my new coffee truck",
  "Mount a 65 inch TV and hide the wires",
  "Photographer for a 2-hour family beach session",
];

export default function IntakePanel({ initialText = "", invitedName, onConfirm }) {
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [subId, setSubId] = useState("");

  useEffect(() => { setText(initialText); }, [initialText]);
  useEffect(() => { api.get("/categories").then((r) => setCats((r.data || []).filter((c) => !/^test/i.test(c.slug || "")))).catch(() => {}); }, []);

  const identify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/service-requests/identify", { text });
      setResult(data);
      setCatId(data.category?.id || "");
      setSubId(data.subcategory?.id || "");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't analyse that — try again");
    } finally {
      setBusy(false);
    }
  };

  const roots = cats.filter((c) => !c.parent_id && c.kind);
  const subs = cats.filter((c) => c.parent_id === catId);
  const cat = cats.find((c) => c.id === catId);

  return (
    <div className="sr-in" data-testid="intake-panel">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#165a71]">Request a service</div>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[#071b22] leading-[1.05] max-w-2xl">Tell us what you need.</h1>
      <p className="mt-3 text-[#252A34]/70 max-w-xl">Describe it in your own words. We'll find the right kind of pro, ask a few smart questions, and send it to the best matches for estimates.</p>
      {invitedName && <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#165a71]/8 border border-[#165a71]/25 px-4 py-1.5 text-sm" data-testid="invited-provider-chip"><Sparkles className="h-4 w-4 text-[#165a71]" /> {invitedName} will be invited first</div>}

      <div className="mt-8 rounded-3xl border border-slate-200 bg-white shadow-[0_20px_60px_-30px_rgba(7,27,34,0.35)] p-2">
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. The AC in my upstairs bedroom stopped blowing cold air…" className="w-full resize-none rounded-2xl p-4 text-lg outline-none placeholder:text-[#252A34]/35" data-testid="intake-text" />
        <div className="flex items-center justify-between gap-3 px-3 pb-2">
          <span className="text-xs text-[#252A34]/50">{text.length < 5 ? "A sentence or two is perfect" : `${text.length} characters`}</span>
          <button onClick={identify} disabled={busy || text.trim().length < 5} className="rounded-full bg-[#071b22] text-white px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-black disabled:opacity-50 transition-[background-color,transform] active:scale-[0.98]" data-testid="intake-identify-btn">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Finding the right pro…</> : <><Wand2 className="h-4 w-4" /> Continue</>}
          </button>
        </div>
      </div>
      {!result && (
        <div className="mt-5 flex flex-wrap gap-2">
          {EXAMPLES.map((e) => <button key={e} onClick={() => setText(e)} className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs text-[#252A34]/80 hover:border-[#165a71]/40" data-testid="intake-example">{e}</button>)}
        </div>
      )}

      {result && (
        <div className="mt-6 rounded-3xl border border-[#165a71]/25 bg-[#f3f8f9] p-6 sr-in" data-testid="intake-result">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#165a71]"><Sparkles className="h-4 w-4" /> {result.source === "ai" ? "We think you need" : "Best match"}</div>
          <div className="mt-2 font-display text-2xl font-semibold text-[#071b22]" data-testid="intake-result-category">{cat?.name || "Pick a category"}{subId && cats.find((c) => c.id === subId) ? ` · ${cats.find((c) => c.id === subId).name}` : ""}</div>
          {result.clarifying_note && <div className="mt-1 text-sm text-[#252A34]/70" data-testid="intake-note">{result.clarifying_note}</div>}
          {result.alternatives?.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[#252A34]/60">Or maybe:</span>
              {result.alternatives.map((a) => <button key={a.id} onClick={() => { setCatId(a.id); setSubId(""); }} className={`rounded-full border px-3 py-1 ${catId === a.id ? "border-[#165a71] bg-white" : "border-slate-300"}`} data-testid={`intake-alt-${a.slug}`}>{a.name}</button>)}
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <select value={catId} onChange={(e) => { setCatId(e.target.value); setSubId(""); }} className="sr-input" data-testid="intake-category-select">
              <option value="">Choose category…</option>
              {roots.map((c) => <option key={c.id} value={c.id}>{`${c.name} (${cats.some((x) => x.parent_id === c.id && x.kind !== c.kind) ? "local + remote" : c.kind})`}</option>)}
            </select>
            <select value={subId} onChange={(e) => setSubId(e.target.value)} className="sr-input" disabled={!subs.length} data-testid="intake-subcategory-select">
              <option value="">Any specialty</option>
              {subs.map((c) => <option key={c.id} value={c.id}>{c.kind !== cat?.kind ? `${c.name} (${c.kind === "digital" ? "remote" : c.kind})` : c.name}</option>)}
            </select>
          </div>
          <button onClick={() => onConfirm({ intake_text: text, category_id: catId, subcategory_id: subId || null, kind: subs.find((c) => c.id === subId)?.kind || cat?.kind, title: result.title })} disabled={!catId} className="mt-5 rounded-full bg-[#165a71] text-white px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#11485a] disabled:opacity-50" data-testid="intake-confirm-btn">
            Looks right — next questions <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
